import produce from 'immer';
import { types } from './actions';
import type {
    IAdminPayoutRequest,
    IPayoutBank,
    IPayoutCountrySupport,
    IPayoutStripeConfig,
    IPayoutSupportedCountriesData,
    IVerifyAccountResult,
} from '../../types/global/payout.types';

export interface IPayoutState {
    supportedCountries?: IPayoutSupportedCountriesData;
    loadingSupportedCountries: boolean;

    countrySupport?: IPayoutCountrySupport;
    loadingCountrySupport: boolean;

    payoutBanksByKey: Record<string, IPayoutBank[]>;
    loadingPayoutBanksKey?: string;

    stripeConfig?: IPayoutStripeConfig;
    loadingStripeConfig: boolean;

    verifyResult?: IVerifyAccountResult;
    verifyingAccount: boolean;
    verifyError?: unknown;

    adminRequests: IAdminPayoutRequest[];
    adminPagination?: {
        page: number;
        limit: number;
        total: number;
        total_pages: number;
    };
    loadingAdminRequests: boolean;
    processingAdminPayoutId?: string;
}

const initialState: IPayoutState = {
    loadingSupportedCountries: false,
    loadingCountrySupport: false,
    payoutBanksByKey: {},
    loadingStripeConfig: false,
    verifyingAccount: false,
    adminRequests: [],
    loadingAdminRequests: false,
};

export const reducer = (state = initialState, action: { type: string; payload?: any }) => {
    const { type, payload } = action;
    switch (type) {
        case types.GET_SUPPORTED_COUNTRIES:
            return produce(state, draft => {
                draft.loadingSupportedCountries = true;
            });
        case types.GET_SUPPORTED_COUNTRIES_SUCCESS:
            return produce(state, draft => {
                draft.loadingSupportedCountries = false;
                draft.supportedCountries = payload;
            });
        case types.GET_SUPPORTED_COUNTRIES_FAIL:
            return produce(state, draft => {
                draft.loadingSupportedCountries = false;
            });

        case types.GET_COUNTRY_SUPPORT:
            return produce(state, draft => {
                draft.loadingCountrySupport = true;
                draft.countrySupport = undefined;
            });
        case types.GET_COUNTRY_SUPPORT_SUCCESS:
            return produce(state, draft => {
                draft.loadingCountrySupport = false;
                draft.countrySupport = payload;
            });
        case types.GET_COUNTRY_SUPPORT_FAIL:
            return produce(state, draft => {
                draft.loadingCountrySupport = false;
            });

        case types.GET_PAYOUT_BANKS:
            return produce(state, draft => {
                draft.loadingPayoutBanksKey = payload?.cacheKey;
            });
        case types.GET_PAYOUT_BANKS_SUCCESS:
            return produce(state, draft => {
                draft.loadingPayoutBanksKey = undefined;
                draft.payoutBanksByKey[payload.cacheKey] = payload.banks;
            });
        case types.GET_PAYOUT_BANKS_FAIL:
            return produce(state, draft => {
                draft.loadingPayoutBanksKey = undefined;
            });

        case types.GET_STRIPE_CONFIG:
            return produce(state, draft => {
                draft.loadingStripeConfig = true;
            });
        case types.GET_STRIPE_CONFIG_SUCCESS:
            return produce(state, draft => {
                draft.loadingStripeConfig = false;
                draft.stripeConfig = payload;
            });
        case types.GET_STRIPE_CONFIG_FAIL:
            return produce(state, draft => {
                draft.loadingStripeConfig = false;
            });

        case types.VERIFY_ACCOUNT:
            return produce(state, draft => {
                draft.verifyingAccount = true;
                draft.verifyError = undefined;
            });
        case types.VERIFY_ACCOUNT_SUCCESS:
            return produce(state, draft => {
                draft.verifyingAccount = false;
                draft.verifyResult = payload;
            });
        case types.VERIFY_ACCOUNT_FAIL:
            return produce(state, draft => {
                draft.verifyingAccount = false;
                draft.verifyError = payload;
                draft.verifyResult = undefined;
            });
        case types.CLEAR_VERIFY_ACCOUNT:
            return produce(state, draft => {
                draft.verifyResult = undefined;
                draft.verifyError = undefined;
            });

        case types.GET_ADMIN_PAYOUT_REQUESTS:
            return produce(state, draft => {
                draft.loadingAdminRequests = true;
            });
        case types.GET_ADMIN_PAYOUT_REQUESTS_SUCCESS:
            return produce(state, draft => {
                draft.loadingAdminRequests = false;
                draft.adminRequests = payload.items ?? [];
                draft.adminPagination = payload.pagination;
            });
        case types.GET_ADMIN_PAYOUT_REQUESTS_FAIL:
            return produce(state, draft => {
                draft.loadingAdminRequests = false;
            });

        case types.RESOLVE_ADMIN_PAYOUT:
        case types.EXECUTE_ADMIN_PAYOUT:
            return produce(state, draft => {
                draft.processingAdminPayoutId = payload?.requestId ?? payload?.id;
            });
        case types.RESOLVE_ADMIN_PAYOUT_SUCCESS:
        case types.RESOLVE_ADMIN_PAYOUT_FAIL:
        case types.EXECUTE_ADMIN_PAYOUT_SUCCESS:
        case types.EXECUTE_ADMIN_PAYOUT_FAIL:
            return produce(state, draft => {
                draft.processingAdminPayoutId = undefined;
            });

        default:
            return state;
    }
};
